import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import NiceModal from '@ebay/nice-modal-react';
import type { FormEachNode } from '../types/layout';
import { useFormEngineForm, useFormEngineState } from '../context/FormEngineContext';
import { canShowEachAddButton, canShowEachRemoveButton } from '../utils/eachConstraints';
import { createEmptyEachItem } from '../renderers/fields/each/createEmptyEachItem';
import { useEventScope } from '@packages/block-events/react/useEventScope';
import { BlockEventType } from '@packages/block-events/core/constants';

/**
 * Generates stable pseudo-IDs for primitive array items so React can key them.
 * RHF's useFieldArray does NOT support primitive (flat) arrays, so for those
 * we manage the array via form.setValue and track IDs ourselves.
 */
let primitiveIdCounter = 0;
function nextPrimitiveId(): string {
  return `__prim_${++primitiveIdCounter}`;
}

export function useFormEachArray(node: FormEachNode) {
  const { form } = useFormEngineForm();
  const { isReadOnly, meta } = useFormEngineState();
  const scope = useEventScope();
  const seededRef = useRef(false);

  const isPrimitive = node.primitiveItem === true;

  // ─── Object arrays: use RHF useFieldArray ───────────────────────────
  const { fields: objectFields, append: objectAppend, remove: objectRemove } = useFieldArray({
    control: form.control as never,
    name: isPrimitive ? ('__unused_field_array__' as never) : (node.name as never),
  });

  // ─── Primitive arrays: manual management via setValue ────────────────
  const [primitiveIds, setPrimitiveIds] = useState<string[]>([]);
  const primitiveValues = useWatch({
    control: form.control,
    name: node.name as never,
  }) as unknown as unknown[] | undefined;

  useEffect(() => {
    if (!isPrimitive) return;
    const currentArr = (primitiveValues ?? []) as unknown[];
    if (currentArr.length !== primitiveIds.length) {
      setPrimitiveIds((prev) => {
        if (currentArr.length > prev.length) {
          const newIds = Array.from({ length: currentArr.length - prev.length }, () => nextPrimitiveId());
          return [...prev, ...newIds];
        }
        return prev.slice(0, currentArr.length);
      });
    }
  }, [isPrimitive, primitiveValues, primitiveIds.length]);

  // Unified fields array with { id } shape for keying
  const fields = useMemo(() => {
    if (!isPrimitive) return objectFields;
    return primitiveIds.map((id) => ({ id }));
  }, [isPrimitive, objectFields, primitiveIds]);

  // ─── Shared logic ───────────────────────────────────────────────────
  // Only watch the array itself (not the entire form) to avoid full-form re-renders.
  const allRowValues = useWatch({
    control: form.control,
    name: node.name as never,
  }) as unknown as Record<string, unknown>[] | undefined;

  // Auto-seed on mount
  useEffect(() => {
    if (seededRef.current || isReadOnly || node.autoSeed == null || node.autoSeed === false) {
      return;
    }
    seededRef.current = true;
    const current = form.getValues(node.name as never) as unknown as unknown[] | undefined;
    if (Array.isArray(current) && current.length > 0) {
      return;
    }
    const count = typeof node.autoSeed === 'number' ? node.autoSeed : 1;
    if (isPrimitive) {
      const seeded = Array.from({ length: count }, () => '');
      form.setValue(node.name as never, seeded as never, { shouldDirty: false });
    } else {
      for (let i = 0; i < count; i++) {
        objectAppend(createEmptyEachItem(node) as never);
      }
    }
  }, [objectAppend, form, isReadOnly, node, isPrimitive]);

  const siblingExcludeFields = useMemo(() => {
    return (node.itemObservers ?? [])
      .filter((o) => o.excludeSiblings)
      .map((o) => o.watch);
  }, [node.itemObservers]);

  const showAdd = canShowEachAddButton(node, fields.length, isReadOnly, form.getValues() as Record<string, unknown>, meta);

  const canRemoveRow = useCallback(
    () => canShowEachRemoveButton(node, fields.length, isReadOnly, form.getValues() as Record<string, unknown>, meta),
    [fields.length, isReadOnly, meta, node, form],
  );

  const publishArrayEvent = useCallback(
    (action: 'add' | 'remove', index: number, count: number) => {
      if (!scope) return;
      scope.bus.publish(BlockEventType.ARRAY_CHANGED, `form:array:${node.name}`, {
        arrayName: node.name,
        action,
        index,
        count,
      });
    },
    [scope, node.name],
  );

  const handleAdd = useCallback(() => {
    if (node.addModalId) {
      NiceModal.show(node.addModalId, {
        onAdd: (item: Record<string, unknown>) => {
          objectAppend(item as never);
          publishArrayEvent('add', fields.length, 1);
        },
      });
      return;
    }
    if (isPrimitive) {
      const current = (form.getValues(node.name as never) as unknown as unknown[] | undefined) ?? [];
      form.setValue(node.name as never, [...current, ''] as never, { shouldDirty: true });
      publishArrayEvent('add', current.length, 1);
      return;
    }
    objectAppend(createEmptyEachItem(node) as never);
    publishArrayEvent('add', fields.length, 1);
  }, [objectAppend, node, isPrimitive, form, fields.length, publishArrayEvent]);

  const handleRemove = useCallback(
    (index: number) => {
      if (isPrimitive) {
        const current = (form.getValues(node.name as never) as unknown as unknown[] | undefined) ?? [];
        const updated = current.filter((_, i) => i !== index);
        form.setValue(node.name as never, updated as never, { shouldDirty: true });
        setPrimitiveIds((prev) => prev.filter((_, i) => i !== index));
        publishArrayEvent('remove', index, 1);
        return;
      }
      objectRemove(index);
      publishArrayEvent('remove', index, 1);
    },
    [objectRemove, isPrimitive, form, node.name, publishArrayEvent],
  );

  return {
    fields,
    append: isPrimitive
      ? ((val: unknown) => {
          const current = (form.getValues(node.name as never) as unknown as unknown[] | undefined) ?? [];
          form.setValue(node.name as never, [...current, val] as never, { shouldDirty: true });
        })
      : objectAppend,
    remove: isPrimitive
      ? ((index: number) => handleRemove(index))
      : objectRemove,
    handleAdd,
    handleRemove,
    showAdd,
    canRemoveRow,
    allRowValues,
    siblingExcludeFields,
  };
}
